import { onearlyRequest } from '@echo/api-client/client';
import { createGroup, listMyGroups, listSubGroups, type MyGroup } from '@echo/api-client/onearly';
import type { GroupPerson, GroupRole } from './group-access';
import { GROUP_ADMIN_ROLE } from './group-membership';
import type { InviteDraft, InviteRole, PendingInvite } from './invites';

/**
 * The groups people create in Stronger Memory: Core sub-groups of the Stronger Memory group
 * (core#737, one level deep). Whoever creates one is its Admin, which the app calls Group
 * Leader; a Group Leader may also hold the group's own Facilitator role (core#745, sub-groups
 * design decision 12.1), and everyone else holds Member.
 *
 * Creating, listing and the caller's permissions are REST (`/api/v1/groups`,
 * `/api/v1/users/me/groups`); roles, members and invitations are GraphQL-only on Core.
 */
export const FACILITATOR_ROLE = 'Facilitator';
export const GROUP_MEMBER_ROLE = 'Member';

/** Inviting, changing roles and removing people: what a Group Leader or Facilitator holds. */
export const MANAGE_MEMBERS_PERMISSION = 'manage-group-members';

export interface StrongerMemoryGroup {
  actorUuid: string;
  name: string;
  /** Whether the person may invite and manage its members (they lead it). */
  manages: boolean;
}

/** The caller's groups that sit under the Stronger Memory group, in Core's order (by name). */
export function strongerMemoryGroups(
  groups: readonly MyGroup[],
  parentActorUuid: string,
): StrongerMemoryGroup[] {
  return groups
    .filter((group) => group.parentGroupActorUuid === parentActorUuid)
    .map((group) => ({
      actorUuid: group.groupActorUuid,
      name: group.name,
      manages: group.permissions.includes(MANAGE_MEMBERS_PERMISSION),
    }));
}

/** Core's role names for a person, as the app's titles. */
export function roleFromRoleNames(names: readonly string[]): GroupRole {
  if (!names.includes(GROUP_ADMIN_ROLE)) return 'member';
  return names.includes(FACILITATOR_ROLE) ? 'facilitator' : 'leader';
}

/** The role names a title is held as: a Facilitator keeps Admin beside Facilitator. */
export function roleNamesFor(role: GroupRole): string[] {
  if (role === 'facilitator') return [GROUP_ADMIN_ROLE, FACILITATOR_ROLE];
  if (role === 'leader') return [GROUP_ADMIN_ROLE];
  return [GROUP_MEMBER_ROLE];
}

export interface GroupRoleRef {
  uuid: string;
  name: string;
}

/** The group's role ids for a title; undefined when the group lacks one of them. */
export function roleUuidsFor(
  role: GroupRole,
  roles: readonly GroupRoleRef[],
): string[] | undefined {
  const uuids = roleNamesFor(role).map((name) => roles.find((r) => r.name === name)?.uuid);
  return uuids.every((uuid): uuid is string => uuid !== undefined) ? uuids : undefined;
}

const HANDLE_MAX = 32;

/**
 * A handle for a new group (Core needs one, unique worldwide; nobody sees it in the app): the
 * name in lowercase letters and digits, then `suffix` so two groups with one name differ.
 */
export function groupHandle(name: string, suffix: string): string {
  const base = name
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, HANDLE_MAX)
    .replace(/-+$/, '');
  return `${base || 'group'}-${suffix}`;
}

function randomSuffix(): string {
  return Math.random().toString(36).slice(2, 8).padEnd(6, '0');
}

const PAGE = 100;

/** Every group the caller belongs to under the Stronger Memory group. */
export async function loadStrongerMemoryGroups(
  parentActorUuid: string,
): Promise<StrongerMemoryGroup[]> {
  const groups: MyGroup[] = [];
  let after: string | undefined;
  do {
    const response = await listMyGroups({ first: PAGE, ...(after ? { after } : {}) });
    if (response.status !== 200) {
      throw new Error(`OnEarly: listing my groups failed with status ${response.status}`);
    }
    groups.push(...response.data.groups);
    after = response.data.nextCursor ?? undefined;
  } while (after);
  return strongerMemoryGroups(groups, parentActorUuid);
}

const HANDLE_ATTEMPTS = 3;

/**
 * What a group's Members may see of each other (core#748): who they are, with names, pictures
 * and roles, and how many. Never emails, which Core refuses to give Members at creation.
 */
export const MEMBER_PERMISSIONS = ['view-group-member-names', 'view-group-member-count'] as const;

/**
 * Creates a group under the Stronger Memory group, with the caller as its Group Leader, a
 * Facilitator role for them to take on, and Members who see each other (`MEMBER_PERMISSIONS`),
 * so everyone in it sees the group's progress. Private (Core's default): people join by
 * invitation.
 * A taken handle is tried again with another suffix. Returns the new group's actor id.
 */
export async function createStrongerMemoryGroup(
  name: string,
  parentActorUuid: string,
  suffix: () => string = randomSuffix,
): Promise<string> {
  const trimmed = name.trim();
  for (let attempt = 1; ; attempt++) {
    try {
      const response = await createGroup({
        name: trimmed,
        id: groupHandle(trimmed, suffix()),
        parentGroupActorUuid: parentActorUuid,
        initialRoles: [{ name: FACILITATOR_ROLE }],
        memberPermissions: [...MEMBER_PERMISSIONS],
      });
      if (response.status === 201) return response.data.group.groupActorUuid;
      throw new Error(`OnEarly: creating the group failed with status ${response.status}`);
    } catch (error) {
      if (attempt >= HANDLE_ATTEMPTS || !isHandleTaken(error)) throw error;
    }
  }
}

/** The api client throws `ApiError` for a non-2xx answer, with Core's error body on it. */
function isHandleTaken(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const { status, body } = error as { status?: unknown; body?: unknown };
  const code =
    typeof body === 'object' && body !== null ? (body as { code?: unknown }).code : undefined;
  return status === 409 && code === 'GROUP_ID_ALREADY_EXISTS';
}

/** A GraphQL answer that refuses the person; `status` is what REST would have said. */
export class GraphqlRefusedError extends Error {
  constructor(
    message: string,
    readonly status: 401 | 403 | 404,
  ) {
    super(message);
    this.name = 'GraphqlRefusedError';
  }
}

/** Core's GraphQL errors carry no HTTP status: read the refusal from the code or the message. */
export function graphqlRefusalStatus(error: {
  message: string;
  extensions?: { code?: unknown };
}): 401 | 403 | 404 | undefined {
  const text = `${typeof error.extensions?.code === 'string' ? error.extensions.code : ''} ${error.message}`;
  if (/unauthenticated/i.test(text)) return 401;
  if (/forbidden|unauthori[sz]ed|permission|not allowed/i.test(text)) return 403;
  if (/not[ _-]?found/i.test(text)) return 404;
  return undefined;
}

async function graphql<T>(what: string, query: string, variables: object): Promise<T> {
  const response = await onearlyRequest<{
    data?: { errors?: Array<{ message: string; extensions?: { code?: unknown } }>; data?: T };
  }>('/graphql', { method: 'POST', body: JSON.stringify({ query, variables }) });
  const first = response.data?.errors?.[0];
  if (first) {
    const message = `OnEarly: ${what} failed — ${first.message}`;
    const status = graphqlRefusalStatus(first);
    throw status ? new GraphqlRefusedError(message, status) : new Error(message);
  }
  const data = response.data?.data;
  if (!data) throw new Error(`OnEarly: ${what} returned nothing`);
  return data;
}

interface MembershipNode {
  joinedAt: string;
  user: { uuid: string; name: string };
  roles: Array<{ role: { name: string } }>;
}

/** Turns Core's memberships into the app's people; `meUuid` marks the person using the app. */
export function toGroupPeople(
  memberships: readonly MembershipNode[],
  meUuid: string | undefined,
): GroupPerson[] {
  return memberships.map((membership) => {
    const you = membership.user.uuid === meUuid;
    return {
      id: membership.user.uuid,
      name: you ? '' : membership.user.name,
      role: roleFromRoleNames(membership.roles.map(({ role }) => role.name)),
      joinedAt: membership.joinedAt,
      ...(you ? { you: true } : {}),
    };
  });
}

const PEOPLE_QUERY = `query People($input: GetGroupMembershipsInput!) {
  getGroupMemberships(input: $input) {
    edges { node { joinedAt user { uuid name } roles { role { name } } } }
    pageInfo { hasNextPage endCursor }
  }
}`;

/**
 * Everyone in the group, with their titles. Needs `view-group-members`, which a Group Leader
 * holds; members of the group can't list each other until Core adds member names (core#748).
 */
export async function loadGroupPeople(
  groupActorUuid: string,
  meUuid: string | undefined,
): Promise<GroupPerson[]> {
  const memberships: MembershipNode[] = [];
  let after: string | undefined;
  do {
    const data = await graphql<{
      getGroupMemberships: {
        edges: Array<{ node: MembershipNode }>;
        pageInfo: { hasNextPage: boolean; endCursor?: string | null };
      };
    }>('listing the members', PEOPLE_QUERY, {
      input: { groupActorUuid, first: PAGE, ...(after ? { after } : {}) },
    });
    const page = data.getGroupMemberships;
    memberships.push(...page.edges.map((edge) => edge.node));
    after = page.pageInfo.hasNextPage ? (page.pageInfo.endCursor ?? undefined) : undefined;
  } while (after);
  return toGroupPeople(memberships, meUuid);
}

/** The group's roles: Admin, Member and its own (Facilitator). Members of the group only. */
export async function loadGroupRoles(groupActorUuid: string): Promise<GroupRoleRef[]> {
  const data = await graphql<{ getGroupRoles: { edges: Array<{ node: GroupRoleRef }> } }>(
    'reading the roles',
    'query Roles($input: GetGroupRolesInput!) { getGroupRoles(input: $input) { edges { node { uuid name } } } }',
    { input: { groupActorUuid, first: PAGE } },
  );
  return data.getGroupRoles.edges.map((edge) => edge.node);
}

/** Gives `personActorUuid` the title `role`; Core refuses taking Admin from its last holder. */
export async function setGroupRole(
  groupActorUuid: string,
  personActorUuid: string,
  role: GroupRole,
  roles: readonly GroupRoleRef[],
): Promise<void> {
  const groupRoleUuids = roleUuidsFor(role, roles);
  if (!groupRoleUuids) throw new Error(`OnEarly: the group has no role for ${role}`);
  await graphql(
    'changing the role',
    'mutation SetRole($input: SetGroupMembershipInput!) { setGroupMembership(input: $input) { membership { groupActorUuid } } }',
    { input: { groupActorUuid, userActorUuid: personActorUuid, groupRoleUuids } },
  );
}

/** Removes someone, or leaves the group when it is the person themselves. */
export async function removeGroupMember(
  groupActorUuid: string,
  personActorUuid: string,
): Promise<void> {
  await graphql(
    'removing the member',
    'mutation Remove($input: RemoveGroupMemberInput!) { removeGroupMember(input: $input) { success } }',
    { input: { groupActorUuid, userActorUuid: personActorUuid } },
  );
}

/** Gives the group a new name, without the spaces around it. Core lets only its Admins. */
export async function renameGroup(groupActorUuid: string, name: string): Promise<void> {
  await graphql(
    'renaming the group',
    'mutation Rename($input: UpdateGroupInput!) { updateGroup(input: $input) { groupActorUuid } }',
    { input: { groupActorUuid, name: name.trim() } },
  );
}

export async function deleteGroup(groupActorUuid: string): Promise<void> {
  await graphql(
    'deleting the group',
    'mutation Delete($input: DeleteGroupInput!) { deleteGroup(input: $input) { __typename } }',
    { input: { groupActorUuid } },
  );
}

interface InviteNode {
  uuid: string;
  invitedUserActorUuid: string;
  email?: string | null;
  phoneNumber?: string | null;
  createdAt: string;
  expiresAt: string;
  roles: Array<{ name: string }>;
}

/**
 * Core's pending invitations as the app's: leaders are invited with Admin. Core shows an
 * invitee's phone number only to the person who sent the invitation, so for anyone else one sent
 * by text message has no address.
 */
export function toPendingInvites(invites: readonly InviteNode[]): PendingInvite[] {
  return invites.map((invite) => ({
    id: invite.uuid,
    inviteeId: invite.invitedUserActorUuid,
    address: invite.email ?? invite.phoneNumber ?? '',
    role: invite.roles.some((role) => role.name === GROUP_ADMIN_ROLE) ? 'leader' : 'member',
    invitedAt: invite.createdAt,
    expiresAt: invite.expiresAt,
  }));
}

/** Invitations sent and not yet answered. Needs `manage-group-members`. */
export async function loadPendingInvites(groupActorUuid: string): Promise<PendingInvite[]> {
  const data = await graphql<{ getGroupInvites: { edges: Array<{ node: InviteNode }> } }>(
    'listing the invitations',
    `query Invites($input: GetGroupInvitesInput!) {
      getGroupInvites(input: $input) { edges { node { uuid invitedUserActorUuid email phoneNumber createdAt expiresAt roles { name } } } }
    }`,
    { input: { groupActorUuid, first: PAGE, statuses: ['PENDING'] } },
  );
  return toPendingInvites(data.getGroupInvites.edges.map((edge) => edge.node));
}

/** Sends one invitation; Core messages the address with a link to join. */
export async function inviteToGroup(
  groupActorUuid: string,
  invite: Pick<InviteDraft, 'channel' | 'address' | 'role'>,
  roles: readonly GroupRoleRef[],
): Promise<void> {
  const role: InviteRole = invite.role;
  const groupRoleUuids = roleUuidsFor(role, roles);
  if (!groupRoleUuids) throw new Error(`OnEarly: the group has no role for ${role}`);
  await graphql(
    'sending the invitation',
    'mutation Invite($input: InviteUserToGroupInput!) { inviteUserToGroup(input: $input) { invite { uuid } } }',
    {
      input: {
        groupActorUuid,
        groupRoleUuids,
        ...(invite.channel === 'email' ? { email: invite.address } : { phone: invite.address }),
      },
    },
  );
}

/**
 * Sends a pending invitation again, by email or text message as it was first sent. Needs
 * `manage-group-members`, like inviting.
 */
export async function resendInvite(
  groupActorUuid: string,
  invite: Pick<PendingInvite, 'id' | 'inviteeId'>,
): Promise<void> {
  await graphql(
    'sending the invitation again',
    'mutation Resend($input: ResendGroupInviteEmailInput!) { resendGroupInviteEmail(input: $input) { success } }',
    { input: { groupActorUuid, inviteUuid: invite.id, userActorUuid: invite.inviteeId } },
  );
}

/**
 * Withdraws a pending invitation, expired or not: its link no longer joins the group. Needs
 * `manage-group-members`, like inviting.
 */
export async function withdrawInvite(inviteUuid: string): Promise<void> {
  await graphql(
    'withdrawing the invitation',
    'mutation Withdraw($input: RevokeGroupInviteInput!) { revokeGroupInvite(input: $input) { revokedInvite { uuid } } }',
    { input: { inviteUuid } },
  );
}

/** The person's title in a group: from its people when known, else from what they may do. */
export function yourRole(
  people: readonly GroupPerson[] | undefined,
  group: Pick<StrongerMemoryGroup, 'manages'>,
): GroupRole {
  const you = people?.find((person) => person.you);
  if (you) return you.role;
  return group.manages ? 'leader' : 'member';
}

/** Where an invitation stands for the person it was sent to. */
export type InvitationStatus = 'pending' | 'accepted' | 'closed';

/** An invitation to a group, as the invited person sees it (Figma Join group). */
export interface GroupInvitation {
  id: string;
  groupActorUuid: string;
  groupName: string;
  /** Absent while Core doesn't show someone outside the group its size. */
  memberCount?: number;
  invitedBy?: string;
  role: InviteRole;
  status: InvitationStatus;
}

interface MyInviteNode {
  uuid: string;
  status: string;
  roles: Array<{ name: string }>;
  invitedBy?: { name: string } | null;
  group: { uuid: string; name: string; memberCount?: number | null };
}

/** Core's invite as the app's: declined, expired and revoked ones can no longer be answered. */
export function toGroupInvitation(node: MyInviteNode): GroupInvitation {
  const status: InvitationStatus =
    node.status === 'PENDING' ? 'pending' : node.status === 'ACCEPTED' ? 'accepted' : 'closed';
  return {
    id: node.uuid,
    groupActorUuid: node.group.uuid,
    groupName: node.group.name,
    ...(typeof node.group.memberCount === 'number' ? { memberCount: node.group.memberCount } : {}),
    ...(node.invitedBy?.name ? { invitedBy: node.invitedBy.name } : {}),
    role: node.roles.some((role) => role.name === GROUP_ADMIN_ROLE) ? 'leader' : 'member',
    status,
  };
}

const ALL_INVITE_STATUSES = ['PENDING', 'ACCEPTED', 'DECLINED', 'EXPIRED', 'REVOKED'];

/**
 * One invitation sent to the signed-in person, by its id (the link's `/invite/{uuid}`), in any
 * status so an answered one can say so. Null when it isn't theirs: Core matches invitations to
 * the account's email or phone.
 */
export async function loadMyInvite(inviteUuid: string): Promise<GroupInvitation | null> {
  const data = await graphql<{ getMyInvites: { edges: Array<{ node: MyInviteNode }> } }>(
    'reading the invitation',
    `query MyInvite($input: GetMyInvitesInput!) {
      getMyInvites(input: $input) {
        edges { node { uuid status roles { name } invitedBy { name } group { uuid name memberCount } } }
      }
    }`,
    { input: { inviteUuid, first: 1, statuses: ALL_INVITE_STATUSES } },
  );
  const [edge] = data.getMyInvites.edges;
  return edge ? toGroupInvitation(edge.node) : null;
}

/** How many pages of the Stronger Memory group's sub-groups an invitation's group is looked for in. */
const SUB_GROUP_PAGES = 20;

/**
 * The invitations waiting for the signed-in person's answer to a Stronger Memory group (Groups ›
 * "You've been invited to join"), newest first as Core returns them. Core's invitations don't
 * say which group a group sits under, so they are matched against the Stronger Memory group's
 * sub-groups, which anyone who can see that PUBLIC group may list: an invitation to any other
 * OnEarly group stays out. The listing stops once every invitation's group is found.
 */
export async function loadMyPendingInvites(parentActorUuid: string): Promise<GroupInvitation[]> {
  const data = await graphql<{ getMyInvites: { edges: Array<{ node: MyInviteNode }> } }>(
    'reading your invitations',
    `query MyPendingInvites($input: GetMyInvitesInput!) {
      getMyInvites(input: $input) {
        edges { node { uuid status roles { name } invitedBy { name } group { uuid name memberCount } } }
      }
    }`,
    { input: { first: 20, statuses: ['PENDING'] } },
  );
  const invites = data.getMyInvites.edges.map((edge) => toGroupInvitation(edge.node));
  if (invites.length === 0) return [];
  const wanted = new Set(invites.map((invite) => invite.groupActorUuid));
  const ours = new Set<string>();
  let after: string | undefined;
  for (let page = 0; page < SUB_GROUP_PAGES && ours.size < wanted.size; page += 1) {
    const response = await listSubGroups(parentActorUuid, {
      first: PAGE,
      ...(after ? { after } : {}),
    });
    if (response.status !== 200) {
      throw new Error(`OnEarly: listing the sub-groups failed with status ${response.status}`);
    }
    for (const group of response.data.subGroups) {
      if (wanted.has(group.groupActorUuid)) ours.add(group.groupActorUuid);
    }
    after = response.data.nextCursor ?? undefined;
    if (!after) break;
  }
  return invites.filter((invite) => ours.has(invite.groupActorUuid));
}

/**
 * A Stronger Memory group the person is not in, by name, from the sub-groups Core lets anyone
 * who can see the Stronger Memory group list. Null for a SECRET group, and where Core doesn't
 * list it (one that's gone, or one under another parent).
 */
export async function findVisibleSubGroup(
  parentActorUuid: string,
  groupActorUuid: string,
): Promise<{ actorUuid: string; name: string } | null> {
  let after: string | undefined;
  for (let page = 0; page < SUB_GROUP_PAGES; page += 1) {
    const response = await listSubGroups(parentActorUuid, {
      first: PAGE,
      ...(after ? { after } : {}),
    });
    if (response.status !== 200) {
      throw new Error(`OnEarly: listing the sub-groups failed with status ${response.status}`);
    }
    const group = response.data.subGroups.find((sub) => sub.groupActorUuid === groupActorUuid);
    // Core lists SECRET groups to the Stronger Memory group's Admins too.
    if (group)
      return group.visibility === 'SECRET'
        ? null
        : { actorUuid: group.groupActorUuid, name: group.name };
    after = response.data.nextCursor ?? undefined;
    if (!after) break;
  }
  return null;
}

/** Joins the group with the roles the invitation carries. */
export async function acceptInvite(inviteUuid: string): Promise<void> {
  await graphql(
    'joining the group',
    'mutation Accept($input: AcceptGroupInviteInput!) { acceptGroupInvite(input: $input) { invite { uuid } } }',
    { input: { inviteUuid } },
  );
}

const INVITE_PATH =
  /(?:^|\/)invite\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?(?:[?#]|$)/i;

/**
 * The invitation id in a link Core sends (`{app}/invite/{uuid}`), whether it arrives as a web
 * address, the app's own scheme or a bare path. Undefined for any other link.
 */
export function inviteIdFromLink(link: string): string | undefined {
  // The app's scheme reads `invite` as the host (`scheme://invite/{uuid}`), the web as a path.
  const match = INVITE_PATH.exec(link);
  return match?.[1]?.toLowerCase();
}
